'use client';

import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { Category } from '@/api/games';
import { GamesGrid } from '@/components/GamesGrid';
import { defaultGridOptions } from '@/components/GamesGrid/helpers';
import { ImageComponent } from '@/components/Image';
import { SectionTitle } from '@/components/SectionTitle';
import { RestrictedProviders } from '@/helpers/conf';
import { GameStaticCategories } from '@/helpers/game';
import { oneRowSliderParams } from '@/helpers/games';
import { useGameThemes } from '@/hooks/game';
import { EmptyGameList } from '@/modules/games/components';
import { PER_CHUNK_CONFIG } from '@/modules/games/helpers';
import { FilterGamesProps } from '@/modules/lobby/components/FilterGames';
import { GamesGridBlockStatic } from '@/modules/lobby/components/FilterGames/Search/components/GameGridBlock';
import LobbyGamesSlider from '@/modules/lobby/components/LobbyGamesSlider';
import { useCMSGameCategories } from '@/modules/lobby/hooks';
import { useCommonStore } from '@/store/commonStore';

import {
  ScContainer,
  ScGamesSection,
  ScLobbySliderWrapper,
} from '@/styles/common';

interface IGamesProps extends FilterGamesProps {}

export const Games: FC<IGamesProps> = ({
  categoryId,
  providerId,
  subCategoryId,
}) => {
  const { t } = useTranslation();

  const { categories: gameCategoriesList } = useCMSGameCategories();

  const {
    isAuthenticated,
    providers,
    recentGames,
    favoriteGames,
    pendingGamesByCategories,
    pendingRecentGames,
    pendingFavoriteGames,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      providers: state.providers,
      recentGames: state.recentGames,
      favoriteGames: state.favoriteGames,
      pendingGamesByCategories: state.pendingGamesByCategories,
      pendingRecentGames: state.pendingRecentGames,
      pendingFavoriteGames: state.pendingFavoriteGames,
    }))
  );

  const { currentGameTheme } = useGameThemes(
    categoryId && decodeURIComponent(categoryId)
  );

  const { isFavoritesCategory, isRecentCategory } = useMemo(
    () => ({
      isFavoritesCategory: categoryId === GameStaticCategories.Favorites,
      isRecentCategory: categoryId === GameStaticCategories.Recent,
    }),
    [categoryId]
  );

  const gamesStatic = useMemo(() => {
    if (!categoryId) return [];

    if (isFavoritesCategory) return favoriteGames;

    if (isRecentCategory) return recentGames;
  }, [
    isFavoritesCategory,
    isRecentCategory,
    recentGames,
    favoriteGames,
    categoryId,
  ]);

  const category = useMemo(() => {
    return gameCategoriesList.find(
      ({ defaultCategoryId }) => defaultCategoryId === categoryId
    );
  }, [categoryId, gameCategoriesList]);

  const gamesGridCategoryId = useMemo(() => {
    if (subCategoryId) return subCategoryId;
    if (categoryId) {
      const decodedCategoryId = decodeURIComponent(categoryId);
      if (decodedCategoryId.startsWith('@')) return decodedCategoryId;
    }
    if (category) return category.id;
    return categoryId;
  }, [gameCategoriesList, isAuthenticated, category, categoryId]);

  const provider = useMemo(() => {
    return providers.find(({ id }) => id === providerId);
  }, [providers, providerId]);

  const hasSubCategories = useMemo(
    () => category?.subCategories.length && !subCategoryId,
    [category, subCategoryId]
  );

  const currentTitle = useMemo(() => {
    if (provider) {
      if (provider.id !== RestrictedProviders.AUTOPAY) return provider.title;
    }

    if (categoryId && pendingGamesByCategories[categoryId])
      return t('common:loadingEllipsis');

    if (categoryId === GameStaticCategories.Favorites && category)
      return typeof category.title === 'string' ? (
        category.title
      ) : (
        <span className="title">
          {t('common:favorites')}&nbsp;
          <span className="after-title">❤️</span>
        </span>
      );

    if (categoryId === GameStaticCategories.Recent && category)
      return typeof category.title === 'string' ? (
        category.title
      ) : (
        <span className="title">
          {t('common:gameCategories:recent')}&nbsp;
          <span className="after-title">🕔</span>
        </span>
      );

    if (categoryId === Category.WAGERING) {
      return (
        <span className="title">
          {t('common:gameCategories.wagering')}&nbsp;
          <span className="after-title">
            <ImageComponent
              src="/wageringIcon.png"
              width={32}
              height={32}
              alt="wagering"
            />
          </span>
        </span>
      );
    }

    if (categoryId === Category.EXCLUSIVE) {
      return (
        <span className="title">
          {t('common:gameCategories.exclusive')}&nbsp;
          <ImageComponent
            width={24}
            height={24}
            src="/exclusiveIcon.webp"
            alt="icon"
          />
        </span>
      );
    }

    if (category && subCategoryId) {
      return category.subCategories.find(
        (subCategory) => subCategory.categoryId === subCategoryId
      )?.title;
    }

    if (category) {
      return category.title;
    }

    if (currentGameTheme) {
      return currentGameTheme.title;
    }

    return null;
  }, [
    provider,
    category,
    currentGameTheme,
    categoryId,
    subCategoryId,
    t,
    pendingGamesByCategories,
  ]);

  return (
    <ScContainer>
      <ScGamesSection>
        {!hasSubCategories && <SectionTitle>{currentTitle}</SectionTitle>}

        {hasSubCategories ? (
          <ScLobbySliderWrapper gap={64}>
            {category?.subCategories.map(
              ({ categoryId, title, description }) => (
                <LobbyGamesSlider
                  key={categoryId}
                  categoryId={category.id}
                  title={title}
                  description={description}
                  sliderParams={oneRowSliderParams}
                  lazyLoadingOf={2}
                  subCategoryId={categoryId}
                  titleMultiRow
                />
              )
            )}
          </ScLobbySliderWrapper>
        ) : isFavoritesCategory || isRecentCategory ? (
          gamesStatic?.length ? (
            <GamesGridBlockStatic
              games={gamesStatic}
              loading={pendingRecentGames || pendingFavoriteGames}
              perChunkObj={PER_CHUNK_CONFIG}
              gridOptions={defaultGridOptions}
              plugGray
            />
          ) : (
            <EmptyGameList categoryId={categoryId} />
          )
        ) : gamesGridCategoryId || providerId ? (
          <GamesGrid
            perChunkObj={PER_CHUNK_CONFIG}
            category={gamesGridCategoryId}
            provider={providerId}
          />
        ) : null}
      </ScGamesSection>
    </ScContainer>
  );
};
